import React, { useEffect, useState } from "react";
import { Modal, Spin, message } from "antd";
import { Elements, useStripe } from "@stripe/react-stripe-js";
import { stripePromise } from "../../../services/stripe";
import {
  CreditCardOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { changePlan, previewPlanChange } from "../subscription.service";
import {
  BillingCycle,
  PaymentMethod,
  Plan,
  PlanChangePreview,
  PlanChangeResult,
} from "../subscription.types";

interface ChangePlanModalProps {
  open: boolean;
  targetPlan: Plan | null;
  cycle: BillingCycle;
  paymentMethod: PaymentMethod | null;
  onClose: () => void;
  onChanged: (result: PlanChangeResult) => void;
  onEditPayment: () => void;
}

const money = (n: number) => `$${n.toFixed(2)}`;
const signed = (item: { amount: number; credit?: boolean }) =>
  `${item.credit ? "− " : "+ "}${money(item.amount)}`;

const ChangePlanForm: React.FC<
  Omit<ChangePlanModalProps, "open"> & {
    submitting: boolean;
    setSubmitting: (v: boolean) => void;
  }
> = ({
  targetPlan,
  cycle,
  paymentMethod,
  onClose,
  onChanged,
  onEditPayment,
  submitting,
  setSubmitting,
}) => {
  const { t } = useTranslation("subscription");
  const stripe = useStripe();
  const [preview, setPreview] = useState<PlanChangePreview | null>(null);
  const [result, setResult] = useState<PlanChangeResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState("");

  useEffect(() => {
    let active = true;
    if (targetPlan) {
      setResult(null);
      setPreview(null);
      // Stable per change-attempt — reused across retries of this same
      // intent so a double-click or a retry-after-failure can't make the
      // backend apply the change twice; a fresh key is drawn whenever the
      // user opens the modal for a (possibly different) plan/cycle.
      setIdempotencyKey(
        `${targetPlan.id}:${cycle}:${crypto.randomUUID()}`,
      );
      setLoading(true);
      previewPlanChange(targetPlan.id, cycle)
        .then((p) => {
          if (active) setPreview(p);
        })
        .catch(() => {
          if (active) {
            message.error(t("changePlan.loadError"));
            onClose();
          }
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }
    return () => {
      active = false;
    };
  }, [targetPlan, cycle]);

  const handleConfirm = async () => {
    if (!targetPlan || !preview) return;
    setSubmitting(true);
    try {
      const res = await changePlan(targetPlan.id, cycle, preview, idempotencyKey);

      // Card (e.g. 3-D Secure) needs the customer to confirm before this is
      // final — the PaymentIntent already has the org's saved card attached
      // server-side, so no `payment_method` is passed here, just the action.
      if (res.requiresAction && res.clientSecret) {
        if (!stripe) {
          message.error(t("changePlan.paymentConfirmationUnavailable"));
          return;
        }
        const confirmResult = await stripe.confirmCardPayment(res.clientSecret);
        if (confirmResult.error) {
          message.error(
            confirmResult.error.message ||
              t("changePlan.cardConfirmationFailed"),
          );
          return; // same idempotencyKey is reused if they retry
        }
        // The subscription flips to active via webhook — give it a moment
        // before the parent refetches (same pattern as the credits top-up).
        await new Promise((r) => setTimeout(r, 2000));
      }

      setResult(res);
      onChanged(res);
    } catch (e: any) {
      // Surface the backend message (e.g. 402 "Downgrade blocked — remove
      // seats/projects first", or a payment error).
      message.error(e?.response?.data?.detail || t("changePlan.confirmError"));
    } finally {
      setSubmitting(false);
    }
  };

  const title = result
    ? ""
    : preview?.requiresPayment
      ? t("changePlan.titles.payment")
      : preview?.direction === "downgrade"
        ? t("changePlan.titles.downgrade")
        : t("changePlan.titles.change");

  return (
    <>
      {loading || !preview ? (
        <div style={{ padding: "48px 0", textAlign: "center" }}>
          <Spin />
        </div>
      ) : result ? (
        // ── Success ───────────────────────────────────────────────────────
        <>
          <div className="sub-success-check">
            <CheckOutlined />
          </div>
          <h3 className="sub-modal-title">
            {t("changePlan.successTitle", { plan: result.planLabel })}
          </h3>
          <p className="sub-modal-sub">
            {t("changePlan.successBody", {
              action:
                preview.direction === "downgrade"
                  ? t("changePlan.actionDowngraded")
                  : t("changePlan.actionUpdated"),
            })}
          </p>
          <div className="sub-summary">
            <div className="sub-summary-row">
              <span className="k">{t("changePlan.summary.plan")}</span>
              <span className="v">{result.planPriceLabel}</span>
            </div>
            <div className="sub-summary-row">
              <span className="k">{t("changePlan.summary.effective")}</span>
              <span className="v">{result.effective}</span>
            </div>
            <div className="sub-summary-row">
              <span className="k">{t("changePlan.summary.cardCharged")}</span>
              <span className="v">{result.cardCharged}</span>
            </div>
            <div className="sub-summary-row">
              <span className="k">{t("changePlan.summary.nextBilling")}</span>
              <span className="v">{result.nextBilling}</span>
            </div>
            <div className="sub-summary-row total">
              <span className="k">{t("changePlan.summary.totalDueToday")}</span>
              <span className="v">{money(result.totalDueToday)}</span>
            </div>
          </div>
        </>
      ) : (
        // ── Preview (downgrade breakdown / payment) ───────────────────────
        <>
          <h3 className="sub-modal-title">{title}</h3>

          {preview.requiresPayment ? (
            <div className="sub-pay-method">
              <div className="sub-pm-main">
                <span className="sub-pm-icon">
                  <CreditCardOutlined />
                </span>
                <div>
                  <div className="sub-pm-kind">{t("common.creditCard")}</div>
                  <div className="sub-pm-detail">
                    {paymentMethod
                      ? t("common.cardEndingIn", {
                          brand: paymentMethod.brand,
                          last4: paymentMethod.last4,
                        })
                      : t("common.noCardOnFile")}
                  </div>
                </div>
              </div>
              <button className="sub-edit-btn" onClick={onEditPayment}>
                {t("common.edit")}
              </button>
            </div>
          ) : (
            <div className="sub-change-head">
              <div className="sub-change-row">
                <span className="k">{t("changePlan.from")}</span>
                <span className="v">
                  {preview.fromLabel} — {preview.fromPriceLabel}
                </span>
              </div>
              <div className="sub-change-row">
                <span className="k">{t("changePlan.to")}</span>
                <span className="v">
                  {preview.toLabel} — {preview.toPriceLabel}
                </span>
              </div>
              <div className="sub-change-row">
                <span className="k">{t("changePlan.summary.effective")}</span>
                <span className="v">{preview.effective}</span>
              </div>
            </div>
          )}

          <div className="sub-change-section-label">
            {t("changePlan.chargedToday")}
          </div>
          {preview.lineItems.map((item) => (
            <div className="sub-change-line" key={item.label}>
              <span className="k">{item.label}</span>
              <span className={`amt${item.credit ? " credit" : ""}`}>
                {signed(item)}
              </span>
            </div>
          ))}

          {preview.creditAppliedToNextInvoice != null && (
            <div className="sub-change-line applied">
              <span className="k">
                {t("changePlan.creditAppliedToNextInvoice")}
              </span>
              <span className="amt">
                {money(preview.creditAppliedToNextInvoice)}
              </span>
            </div>
          )}

          {preview.requiresPayment && (
            <div className="sub-change-total">
              <span>{t("changePlan.summary.totalDueToday")}</span>
              <span>{money(preview.totalDueToday)}</span>
            </div>
          )}

          {preview.creditNote && (
            <div className="sub-note">
              <InfoCircleOutlined className="ic" />
              <span>{preview.creditNote}</span>
            </div>
          )}

          {preview.fromNextCycleLabel && (
            <div className="sub-change-line">
              <span className="k">{t("changePlan.fromNextBillingCycle")}</span>
              <span className="amt">{preview.fromNextCycleLabel}</span>
            </div>
          )}

          {preview.warning && (
            <div className="sub-note danger">
              <WarningOutlined className="ic" />
              <span>{preview.warning}</span>
            </div>
          )}

          <div className="sub-modal-actions">
            <button
              className="sub-btn sub-btn-outline"
              disabled={submitting}
              onClick={onClose}
            >
              {t("common.cancel")}
            </button>
            <button
              className="sub-btn sub-btn-primary"
              disabled={submitting}
              onClick={handleConfirm}
            >
              {submitting
                ? t("changePlan.actions.processing")
                : preview.requiresPayment
                  ? t("changePlan.actions.payNow")
                  : preview.direction === "downgrade"
                    ? t("changePlan.actions.confirmDowngrade")
                    : t("changePlan.actions.confirmChange")}
            </button>
          </div>
        </>
      )}
    </>
  );
};

const ChangePlanModal: React.FC<ChangePlanModalProps> = ({
  open,
  onClose,
  ...rest
}) => {
  const [submitting, setSubmitting] = useState(false);
  return (
    <Modal
      open={open}
      onCancel={() => {
        if (!submitting) onClose();
      }}
      maskClosable={!submitting}
      keyboard={!submitting}
      closable={!submitting}
      footer={null}
      centered
      width={460}
      className="sub-modal"
      destroyOnClose
    >
      <Elements stripe={stripePromise}>
        <ChangePlanForm
          onClose={onClose}
          submitting={submitting}
          setSubmitting={setSubmitting}
          {...rest}
        />
      </Elements>
    </Modal>
  );
};

export default ChangePlanModal;
